import {
  GraphQLObjectType,
  GraphQLString,
  GraphQLList,
  GraphQLNonNull,
  GraphQLID,
  GraphQLFloat,
  GraphQLBoolean
} from 'graphql';
import { toGlobalId } from 'graphql-relay';
import CardQuestionOptionType from './cardQuestionOptionType.gql';
import QuestionResponseType from './questionResponse.gql';
import CardType from './card.gql';
import GradingSchemeEnumType from './enums/gradingScheme.gql';
import PeerSubmissionReviewType from './peerSubmissionReview.gql';
import PeerQuestionForReviewType from './peerQuestionForReview.gql';
import QuestionOpinionEnumType from './enums/questionOpinionEnum.gql';
import PairType from './pairType.gql';
import FTBComponentType from './ftbComponentType.gql';
import Distractor from './distractor.gql';
import { SubmissionAnswerEnum } from './transformToEnums/SubmissionAnswerEnum';

export const GradingSchemeEnum = {
  autoGradable: 'AUTO_GRADABLE',
  peerGradable: 'PEER_GRADABLE',
  teacherGradable: 'TEACHER_GRADABLE'
};

const QuestionOpinionEnum = {
  agreement: 'AGREEMENT',
  comparison: 'COMPARISON',
  frequency: 'FREQUENCY',
  importance: 'IMPORTANCE',
  likelihood: 'LIKELIHOOD',
  quality: 'QUALITY'
};

// TODO : topRatedResponses, peerQuestions, answers

const questionAnswerType = new GraphQLObjectType({
  name: 'QuestionAnswerType',
  fields: () => ({
    id: {
      type: new GraphQLNonNull(GraphQLID),
      resolve: (root) => {
        const rootOfferId = root.rootOfferId.toString();
        const offerId = root._id.toString();
        return toGlobalId('QuestionAnswerType', `${offerId}-${rootOfferId}`);
      }
    },
    pid: {
      type: GraphQLString,
      resolve: root => root._id.toString()
    },
    q: {
      type: GraphQLString,
      resolve: (root) => {
        const content = root.content;
        const question = content.question ? content.question : {};
        return question.questionText;
      }
    },
    rType: {
      type: GraphQLString,
      resolve: (item) => {
        const content = item.content;
        const question = content.question ? content.question : {};

        if (question.responseType) {
          return SubmissionAnswerEnum[question.responseType[0]];
        }
        return null;
      }
    },
    rTypeSecondary: {
      type: new GraphQLList(GraphQLString),
      resolve: (item) => {
        const content = item.content;
        const question = content.question ? content.question : {};

        if (question.responseType) {
          question.responseType = question.responseType.map(type => SubmissionAnswerEnum[type]);
          return question.responseType.slice(1);
        }
        return null;
      }
    },
    options: {
      type: new GraphQLList(CardQuestionOptionType),
      resolve: (root) => {
        try {
          const content = root.content;
          return content.question.options || [];
        } catch (e) {
          return [];
        }
      }
    },
    answer: {
      type: new GraphQLList(QuestionResponseType),
      resolve: (root) => {
        const answers = root.states.length
          ? root.states.find(state => state.contentId === root._id).submissions || []
          : [];
        const answersWithStateInterections = answers.map(answer => ({
          ...answer,
          stateInteractions: root.stateInteractions
        }));
        return answersWithStateInterections;
      }
    },
    card: {
      type: CardType
    },
    minScore: {
      type: GraphQLFloat
    },
    maxScore: {
      type: GraphQLFloat
    },
    rawScore: {
      type: GraphQLFloat,
      resolve: root => (root.states.length
        ? root.states.find(state => state.contentId === root._id).rawScore || 0.0
        : 0.0)
    },
    minGetReviewedCount: {
      type: GraphQLFloat,
      resolve: root => root.minGetReviewedCount
    },
    minReviewCount: {
      type: GraphQLFloat,
      resolve: root => root.minReviewCount
    },
    gradingScheme: {
      type: GradingSchemeEnumType,
      resolve: root => GradingSchemeEnum[root.content.gradingScheme]
    },
    opinionType: {
      type: QuestionOpinionEnumType,
      resolve: (root) => {
        try {
          const content = root.content;
          return QuestionOpinionEnum[content.question.opinionType];
        } catch (e) {
          return [];
        }
      }
    },
    topRatedResponses: {
      type: new GraphQLList(PeerSubmissionReviewType),
      resolve: async root => []
    },
    peerQuestions: {
      type: new GraphQLList(PeerQuestionForReviewType),
      args: {
        userId: {
          type: GraphQLString
        }
      },
      resolve: root => root.peerQuestions
    },
    correctAnswerFeedback: {
      type: GraphQLString,
      resolve: (root) => {
        const content = root.content;
        const question = content.question ? content.question : {};
        return question.correctAnswerFeedback || '';
      }
    },
    incorrectAnswerFeedback: {
      type: GraphQLString,
      resolve: (root) => {
        const content = root.content;
        const question = content.question ? content.question : {};
        return question.incorrectAnswerFeedback || '';
      }
    },
    actualSubmit: {
      type: GraphQLBoolean,
      resolve: root => !!(root.states.length
        ? root.states.find(state => state.contentId === root._id).actualSubmit
        : false)
    },
    contributeToCompleteStatus: {
      type: GraphQLBoolean
    },
    hideAnswers: {
      type: GraphQLBoolean,
      resolve: (root) => {
        try {
          const content = root.content;
          return !!content.question.hideAnswers;
        } catch (e) {
          return false;
        }
      }
    },
    pairs: {
      type: new GraphQLList(PairType),
      resolve: (root) => {
        try {
          const content = root.content;
          return content.question.pairs || [];
        } catch (e) {
          return [];
        }
      }
    },
    ftbComponents: {
      type: new GraphQLList(FTBComponentType),
      resolve: (root) => {
        try {
          const content = root.content;
          return content.question.ftbComponents || [];
        } catch (e) {
          return [];
        }
      }
    },
    globalDistractors: {
      type: new GraphQLList(Distractor),
      resolve: (root) => {
        try {
          const content = root.content;
          return content.question.globalDistractors || [];
        } catch (e) {
          return [];
        }
      }
    }
  })
});

window[questionAnswerType.name + 'GQLType'] = questionAnswerType;

export default questionAnswerType;
